"use client"

import { Button } from "@/components/atoms"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { getProductPrice } from "@/lib/helpers/get-product-price"
import { HttpTypes } from "@medusajs/types"
import { useTranslate } from "@tolgee/react"
import clsx from "clsx"
import { BaseHit, Hit } from "instantsearch.js"
import Image from "next/image"
import cardIcon from "@/icons/card.svg"
import { ButtonAddToCart } from "@/components/custom/Product/AddCart"
import { useState } from "react"
import { useScreenSize } from "@/hooks/useScreenSize"

export const PromotionProductCard = ({
  product = {},
  api_product,
  locale,
  showAddToCartButton = true,
  showVariantCount = false,
  className,
}: {
  product: Hit<HttpTypes.StoreProduct> | Partial<Hit<BaseHit>>
  api_product?: HttpTypes.StoreProduct | null
  locale: string
  showAddToCartButton?: boolean
  showVariantCount?: boolean
  className?: string
}) => {
  const { t } = useTranslate();
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const screenSize = useScreenSize()
  if (!api_product) {
    return <></>
  }

  const { cheapestVariant, cheapestPrice } = getProductPrice({
    product: api_product! as HttpTypes.StoreProduct,
  })

  const isSmallScreen = screenSize === 'sm'
  const isAvailable = cheapestVariant && cheapestPrice?.calculated_price

  return (
    <div
      className={clsx(
        "relative group rounded-sm flex flex-col justify-between w-full lg:max-w-[calc(25%-1rem)] lg:min-w-[220px] max-w-[180px]",
        className
      )}
    >
      <div className="relative w-full h-full bg-primary aspect-square">
        <div className="absolute -top-5 -left-5 z-10 w-12 h-8 sm:w-20 sm:h-10">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="#EE4B2B">
            <g>
              <title>Layer 1</title>
              <ellipse stroke="#ffffff" ry="25.44399" rx="25.78724" id="svg_7" cy="50.98932" cx="50.81768" fill="#ffffff" />
              <path id="svg_1" d="m44.105,55.641l3.598,-2.079l-4.666,-3.925l1.068,6.004z" fill="#EE4B2B" />
              <path id="svg_2" d="m88.558,49.96c0,-0.885 -0.435,-1.663 -1.097,-2.151l0.014,-0.024l-9.324,-5.383l5.367,-9.296l-0.018,-0.011a2.666,2.666 0 0 0 -0.127,-2.408a2.667,2.667 0 0 0 -2.025,-1.314l0,-0.026l-10.768,0l0,-10.737l-0.022,0a2.667,2.667 0 0 0 -1.314,-2.022a2.662,2.662 0 0 0 -2.412,-0.125l-0.013,-0.023l-9.481,5.474l-5.25,-9.094l-0.019,0.011a2.668,2.668 0 0 0 -2.149,-1.094c-0.885,0 -1.664,0.435 -2.151,1.097l-0.024,-0.014l-5.337,9.244l-9.19,-5.306l-0.011,0.019a2.666,2.666 0 0 0 -2.408,0.127a2.666,2.666 0 0 0 -1.315,2.025l-0.027,0l0,10.674l-10.612,0l0,0.021a2.667,2.667 0 0 0 -2.022,1.314a2.667,2.667 0 0 0 -0.126,2.41l-0.023,0.014l5.246,9.087l-9.394,5.424l0.011,0.019a2.668,2.668 0 0 0 -1.094,2.149c0,0.885 0.435,1.664 1.097,2.151l-0.014,0.024l9.324,5.383l-5.367,9.296l0.018,0.01a2.666,2.666 0 0 0 0.127,2.408a2.667,2.667 0 0 0 2.025,1.314l0,0.027l10.767,0l0,10.736l0.022,0c0.092,0.816 0.549,1.58 1.314,2.022a2.665,2.665 0 0 0 2.412,0.125l0.013,0.023l9.481,-5.474l5.25,9.094l0.019,-0.011a2.668,2.668 0 0 0 2.149,1.094c0.885,0 1.664,-0.435 2.151,-1.096l0.023,0.013l5.337,-9.244l9.191,5.306l0.011,-0.019a2.666,2.666 0 0 0 2.408,-0.127a2.664,2.664 0 0 0 1.315,-2.025l0.027,0l0,-10.673l10.613,0l0,-0.021a2.667,2.667 0 0 0 2.022,-1.314a2.667,2.667 0 0 0 0.126,-2.41l0.023,-0.013l-5.246,-9.087l9.394,-5.424l-0.011,-0.019a2.67,2.67 0 0 0 1.094,-2.15zm-51.021,15.237c-2.23,1.288 -4.252,1.464 -5.971,1.002l0.241,-2.697c1.302,0.377 2.985,0.375 4.575,-0.544c1.367,-0.789 1.658,-1.765 1.269,-2.438c-1.159,-2.006 -6.992,3.23 -9.499,-1.111c-1.108,-1.92 -0.367,-4.471 2.35,-6.039c1.833,-1.059 3.675,-1.383 5.426,-0.988l-0.309,2.623c-1.433,-0.324 -2.908,-0.004 -4.084,0.674c-1.038,0.6 -1.367,1.389 -0.967,2.082c1.049,1.816 6.965,-3.236 9.451,1.069c1.219,2.109 0.616,4.58 -2.482,6.367zm13.943,-8.326l-1.854,-1.535l-4.947,2.856l0.401,2.374l-2.785,1.607l-2.215,-14.103l3.079,-1.777l11.106,8.971l-2.785,1.607zm3.753,-2.166l-6.661,-11.538l2.474,-1.429l5.413,9.375l4.878,-2.816l1.249,2.163l-7.353,4.245zm9.012,-5.203l-6.661,-11.537l8.164,-4.715l1.248,2.162l-5.707,3.296l1.398,2.422l5.586,-3.226l1.248,2.162l-5.586,3.225l1.518,2.63l5.708,-3.296l1.249,2.162l-8.165,4.715z" fill="#EE4B2B" />
              <path id="svg_3" d="m32.23885,54.29317" opacity="NaN" stroke="#000" fill="#fff" />
            </g>
          </svg>
        </div>
        <LocalizedClientLink href={`/products/${product.handle}`}>
          <div className="overflow-hidden rounded-sm w-full h-full flex justify-center align-center ">
            {product.thumbnail ? (
              <Image
                src={decodeURIComponent(product.thumbnail)}
                alt={t("product_card.product_placeholder")}
                width={360}
                height={360}
                priority
                className="object-cover aspect-square w-full object-center h-full lg:group-hover:-mt-14 transition-all duration-300 rounded-xs"
              />
            ) : (
              <Image
                src="/images/placeholder.svg"
                alt={t("product_card.product_placeholder")}
                width={100}
                height={100}
              />
            )}
          </div>
        </LocalizedClientLink>
        <LocalizedClientLink href={`/products/${product.handle}`}>
          <Button className="absolute rounded-sm bg-action text-action-on-primary h-auto lg:h-[48px] lg:group-hover:block hidden w-full uppercase bottom-1 z-10">
            {t("product_card.see_more")}
          </Button>
        </LocalizedClientLink>
      </div>
      <div className="flex flex-row justify-between align-top p-3 gap-2">
        <LocalizedClientLink href={`/products/${product.handle}`} className="flex-grow overflow-hidden">
          <div className="w-full">
            <h3 className="heading-sm whitespace-nowrap text-ellipsis overflow-hidden">{product.title}</h3>
            <div className="flex items-center gap-2 mt-2 justify-between">
              <p className="font-medium">{cheapestPrice?.calculated_price}</p>
              {cheapestPrice?.calculated_price !==
                cheapestPrice?.original_price && (
                  <p className="text-sm text-gray-500 line-through">
                    {cheapestPrice?.original_price}
                  </p>
                )}
              {showVariantCount && !isSmallScreen ? (
                (api_product.variants?.length || 0) > 1 ? (
                  <p className="text-xs text-gray-500 mt-1 whitespace-pre">
                    {t("product_card.variants", {
                      count: api_product.variants?.length || 0,
                    })}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1 whitespace-pre">
                    {t("product_card.variants1")}
                  </p>
                )
              ) : null}
            </div>
            {showVariantCount && isSmallScreen ? (
              (api_product.variants?.length || 0) > 1 ? (
                <p className="text-xs text-gray-500 mt-1">
                  {t("product_card.variants", {
                    count: api_product.variants?.length || 0,
                  })}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {t("product_card.variants1")}
                </p>
              )
            ) : null}
          </div>
        </LocalizedClientLink>

        {showAddToCartButton && (
          <ButtonAddToCart
            variant={cheapestVariant}
            title={product.title}
            locale={locale}
            setLoading={setIsAddingToCart}
            disabled={!isAvailable}
            className={
              "shrink-0 w-[68px] h-[42px] rounded-lg bg-[#0B0A09] hover:bg-stone-800 flex flex-row items-center justify-center " +
              (isAvailable ? "text-white" : "text-white-400")
            }
          >
            <Image src={cardIcon} width={20} height={20} alt="" />
          </ButtonAddToCart>
        )}

      </div>
    </div>
  )
}
